import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {
    LoadChallengeVersionsAction,
    LoadingAction,
    SetLibraryCurriculumIdAction,
    SetLibraryGradeAction,
    SetLibraryThemeAction,
    SetLibraryTopicAction
} from "mindsets-js-sdk";

import {getSchoolCurriculumId} from "common/challengeNew/utils";

/**
 * Keeps the Challenge list and the library_* filter selections in sync with the
 * curriculum of the school the user belongs to.
 *
 * The server filters Challenges by the school's curriculum, and the Topic/Grade
 * options are derived from what comes back. Because the selections live in a
 * store level slice that outlives the page, changing school on the profile page
 * would otherwise leave selections from the previous curriculum in place and
 * filter every Challenge out.
 */
export default function useLibraryChallenges() {
    const dispatch = useDispatch();
    const me = useSelector(state => state.me);
    const challenge_versions = useSelector(state => state.challenge_versions);
    const library_curriculum_id = useSelector(state => state.library_curriculum_id);
    const [loading, setLoading] = useState(true);
    const request_id = useRef(0);

    function load(curriculum_id) {
        const id = ++request_id.current;
        setLoading(true);

        return dispatch(LoadChallengeVersionsAction({limit: 0}))
            .then(challenge_versions => {
                // A newer request has been made since, its response wins.
                if (id !== request_id.current) {
                    return;
                }
                // LoadChallengeVersionsAction swallows its own errors and resolves
                // undefined. Only record the curriculum once we actually have a
                // list for it, so a failed request is retried on the next visit.
                if (Array.isArray(challenge_versions)) {
                    dispatch(SetLibraryCurriculumIdAction(curriculum_id));
                }
                setLoading(false);
                dispatch(LoadingAction(false));
            });
    }

    useEffect(() => {
        // Wait for me, the curriculum is not known until it arrives.
        if (!me) {
            return;
        }

        const curriculum_id = getSchoolCurriculumId(me);
        // Deliberately read outside the dependency list. Depending on
        // library_curriculum_id would re-run this effect on our own dispatch
        // below, while the list is still in flight, and fire a second request.
        const changed = curriculum_id !== library_curriculum_id;

        if (changed) {
            dispatch(SetLibraryGradeAction(''));
            dispatch(SetLibraryTopicAction(''));
            dispatch(SetLibraryThemeAction(''));
        }
        if (changed || challenge_versions.length <= 0) {
            load(curriculum_id);
        } else {
            setLoading(false);
        }
    }, [me]);

    // For callers that need to force a refresh, ie on a user.updated broadcast,
    // where the Challenges available may have changed without the curriculum
    // having changed.
    function reload() {
        if (!me) {
            return;
        }

        return load(getSchoolCurriculumId(me));
    }

    return {loading, reload};
}
